// WbPrintButton — Phase C12.
//
// Fetches a server-rendered PDF for a single WB ticket and either opens
// it in a new tab (default) or triggers a download. Uses the same
// fetch→blob→objectURL pattern as the C7 EA waste-export CSV download
// (WbReportsPage). We can't use the generic <PrintTemplateActions>
// here because that component renders templates client-side from
// /api/v1/print-templates; the WB ticket PDF endpoint is a binary
// server-rendered endpoint at GET /wb/tickets/:enc_id/pdf/:template_type.
//
// Auth: the apiClient axios instance attaches the Bearer token via the
// shared request interceptor; using apiClient.raw.get with
// responseType:'blob' keeps the auth header AND bypasses the envelope
// wrapper (the response is a PDF, not the standard JSON envelope).

import { useState } from 'react';
import { Printer } from 'lucide-react';
import { apiClient } from '../../../../api/client';
import { endpoints } from '../../../../api/endpoints';
import { extractErrorMessage } from '../../../../utils/errorMessage';
import { useToastStore } from '../../../../store/toastStore';
import { Button } from '../../../../components/ui/Button/Button';

export type WbPdfTemplateType =
  | 'wb_ticket'
  | 'wb_advice_note'
  | 'wb_wtn'
  | 'wb_hazardous_consignment';

export interface WbPrintButtonProps {
  label: string;
  templateType: WbPdfTemplateType;
  ticketEncId: string;
  /** Optional filename for download. Defaults to {templateType}-{encId}.pdf */
  filename?: string;
}

export function WbPrintButton({
  label,
  templateType,
  ticketEncId,
  filename,
}: WbPrintButtonProps): JSX.Element {
  const [loading, setLoading] = useState(false);
  const pushToast = useToastStore((s) => s.push);

  async function handleClick() {
    setLoading(true);
    try {
      const url = endpoints.wb.ticketPdf(ticketEncId, templateType);
      const res = await apiClient.raw.get(url, { responseType: 'blob' });
      const blob = res.data as Blob;
      const objectUrl = URL.createObjectURL(blob);
      // Open in a new tab so the user can preview and print from the
      // browser's PDF viewer (matches the operator workflow — they print
      // tickets on a thermal/desktop printer via the OS dialog). If the
      // popup is blocked we fall back to a download via <a download=…>.
      const opened = window.open(objectUrl, '_blank', 'noopener,noreferrer');
      if (opened === null) {
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename ?? `${templateType}-${ticketEncId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      // Revoke after a short delay so the new tab has time to load the
      // blob URL. Match the same delay used for the EA waste-export CSV.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      pushToast({
        type: 'error',
        message: extractErrorMessage(err) ?? 'Failed to generate PDF.',
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Button
      variant="secondary"
      icon={Printer}
      onClick={handleClick}
      loading={loading}
      data-testid={`wb-print-${templateType}`}
    >
      {label}
    </Button>
  );
}
